import { Injectable, effect, inject, signal } from "@angular/core";
import { NavigationEnd, Router } from "@angular/router";
import { filter, firstValueFrom } from "rxjs";
import { AuthService } from "./auth.service";
import { LegalService } from "./legal.service";
import {
  LEGAL_DOCUMENTS,
  LegalDocumentType,
  PendingLegalDocument,
} from "../../models/legal.model";

/** Intervalo mínimo entre consultas disparadas por navegação. */
const RECHECK_MS = 60_000;

/**
 * Aceite obrigatório dos documentos jurídicos (spec 029 §3). Guarda o que a
 * pessoa logada ainda precisa aceitar; o modal do layout lê `pending`.
 *
 * Consulta quando a sessão muda (login, F5 com o refresh do cookie, troca de
 * conta) e, no máximo uma vez por minuto, a cada navegação — é assim que uma
 * versão nova publicada com a pessoa já logada chega a ela.
 */
@Injectable({ providedIn: "root" })
export class LegalConsentService {
  private readonly auth = inject(AuthService);
  private readonly legal = inject(LegalService);
  private readonly router = inject(Router);

  readonly pending = signal<PendingLegalDocument[]>([]);

  private lastUserId: string | null = null;
  private lastCheck = 0;
  private inFlight: Promise<void> | null = null;

  constructor() {
    effect(() => {
      const userId = this.auth.isLoggedIn() ? this.auth.getUserId() : null;
      if (userId === this.lastUserId) return;
      this.lastUserId = userId;
      this.pending.set([]);
      if (userId) void this.refresh();
    });

    this.router.events
      .pipe(filter((e): e is NavigationEnd => e instanceof NavigationEnd))
      .subscribe(() => {
        if (this.lastUserId && Date.now() - this.lastCheck > RECHECK_MS) {
          void this.refresh();
        }
      });
  }

  /**
   * Página de documento jurídico: o modal não a cobre — é para lá que os
   * links dele levam, inclusive numa aba nova que restaura a sessão.
   */
  isLegalPage(url: string): boolean {
    const path = url.split(/[?#]/)[0];
    return Object.values(LEGAL_DOCUMENTS).some((doc) => doc.route === path);
  }

  async refresh(): Promise<void> {
    if (this.inFlight) return this.inFlight;
    this.inFlight = (async () => {
      try {
        this.lastCheck = Date.now();
        this.pending.set(await firstValueFrom(this.legal.getPending()));
      } catch {
        // Falha de rede não pode trancar ninguém fora; tenta na próxima navegação.
        this.lastCheck = 0;
      } finally {
        this.inFlight = null;
      }
    })();
    return this.inFlight;
  }

  /** Aceita e guarda o que ainda falta (a resposta já vem atualizada). */
  async accept(types: LegalDocumentType[]): Promise<void> {
    this.pending.set(await firstValueFrom(this.legal.accept(types)));
    this.lastCheck = Date.now();
  }
}
